"use client";

import React, { useEffect, useRef, useState } from "react";

type HorizontalShelfProps<T> = {
  title: string;
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
};

export default function HorizontalShelf<T>({
  title,
  items,
  renderItem,
}: HorizontalShelfProps<T>) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    update();
    const el = railRef.current;
    if (!el) return;

    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // re-run when the list changes size
  }, [items.length]);

  // Wheel -> horizontal scroll on desktop
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      const isMostlyVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      if (!isMostlyVertical) return;

      const canScroll = el.scrollWidth > el.clientWidth;
      if (!canScroll) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const scrollByCards = (dir: -1 | 1) => {
    const el = railRef.current;
    if (!el) return;

    const card = el.querySelector<HTMLElement>("[data-card]");
    const step = card ? card.getBoundingClientRect().width + 16 : 220; // 16 = gap-4
    el.scrollBy({ left: dir * step * 3, behavior: "smooth" });
  };

  if (!items?.length) return null;

  return (
    <div className="mb-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold opacity-90">{title}</h2>

        <div className="hidden sm:flex gap-2">
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            disabled={!canLeft}
            className="rounded-lg border border-border px-2 py-1 text-xs disabled:opacity-40"
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            disabled={!canRight}
            className="rounded-lg border border-border px-2 py-1 text-xs disabled:opacity-40"
          >
            →
          </button>
        </div>

        <span className="sm:hidden text-xs text-textSecondary">Swipe →</span>
      </div>

      <div
        ref={railRef}
        className="flex gap-4 overflow-x-auto pb-2 scroll-smooth
          cursor-grab active:cursor-grabbing
          [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map(renderItem)}
      </div>
    </div>
  );
}
